import { callSelectors, callHistorySelectors, chatSelectors } from '@lifesize/nucleus';
import { setPillNotificationWithTimeout, deletePillNotification } from 'actions/Notification';
import _get from 'lodash/get';
import { createSelector } from 'reselect';
import { defineMessages } from 'react-intl';
import { messagesSelector, formatMessage } from 'observers/utils/intl';
import { Observer } from '@lifesize/web-clients-core';

const initialData = {
  isFullyConnected: false,
  hasAudioDevices: false,
  isMutedByServer: false,
  recorder: null,
  messages: {},
  isModerator: false
};

const messages = defineMessages({
  call: { id: 'notification.call.title', defaultMessage: 'Incoming Call From' },
  moderatorMutedByModerator:  { id: 'notification.moderatorMutedByModerator', defaultMessage: 'Your microphone has been muted. As a moderator, you may unmute yourself.' },
  muteAll: {
    id: 'notification.muteAll',
    defaultMessage: 'The moderator has muted your microphone. You cannot unmute.'
  },
  unlockAll: {
    id: 'notification.unlockAll',
    defaultMessage: 'The moderator has unmuted your microphone.'
  },
  unlockWhileMuted: {
    id: 'notification.unlockWhileMuted',
    defaultMessage: 'The moderator has unlocked your microphone. You are still muted.'
  },
  recordingStarted: { id: 'components.incall.footer.notification.recordingStarted', defaultMessage: '{name} has started recording.' },
  recordingStopped: { id: 'components.incall.footer.notification.recordingStopped', defaultMessage: '{name} has stopped recording.' },
  recordingStartedSelf: { id: 'components.incall.footer.notification.recordingStartedSelf', defaultMessage: 'You have started recording.' },
  recordingStoppedSelf: { id: 'components.incall.footer.notification.recordingStoppedSelf', defaultMessage: 'You have stopped recording.' }
});

function updatePageTitle(missedNotifications) {
  if (missedNotifications) {
    const count = (missedNotifications > 99 ? '99+' : missedNotifications);
    document.title = `(${count}) Lifesize`;
  }
  else {
    document.title = 'Lifesize';
  }
}

const activeCallSelector = state => callSelectors.activeCallSelector(state);
const missedCallsSelector = state => callHistorySelectors.totalNewMissedCalls(state);
const unreadChatsSelector = state =>
  chatSelectors.groupedConversationsWithUnread(state).totalUnreadMessageCount;
const notificationMessagesSelector = state =>
  messagesSelector(state, messages);

const notificationObserverSelector = createSelector(
  [
    activeCallSelector,
    notificationMessagesSelector,
    missedCallsSelector,
    unreadChatsSelector
  ],
  (activeCall, translatedMessages, missedCalls, unreadChats) => {
    const isMutedByServer = _get(activeCall, 'isMutedByServer');
    const recorder = _get(activeCall, 'recorder');
    const wasKicked = _get(activeCall, 'wasKicked');
    const isFullyConnected = _get(activeCall, 'isFullyConnected');
    const isAudioMuted =   _get(activeCall, 'isAudioMuted');
    const isModerator = _get(activeCall, 'isModerator', false);

    return {
      isFullyConnected,
      isMutedByServer,
      isAudioMuted,
      wasKicked,
      recorder,
      messages: translatedMessages,
      missedNotifications: missedCalls + unreadChats,
      isModerator
    };
  });

const recordingHandler = (oldData, newData, dispatch) => {
  const addNotification = message => dispatch(setPillNotificationWithTimeout(message));
  const deleteNotification = () => dispatch(deletePillNotification());

  if (!oldData.recorder && newData.recorder && newData.isFullyConnected) {
    const recordingStartedMessage = newData.recorder.isSelf ?
      newData.messages.recordingStartedSelf :
      formatMessage(
        newData.messages.recordingStarted,
        { name: newData.recorder.name }
      );

    return addNotification(recordingStartedMessage);
  }

  if (oldData.recorder && !newData.recorder && newData.isFullyConnected) {
    const recordingStoppedMessage = oldData.recorder.isSelf ?
      newData.messages.recordingStoppedSelf :
      formatMessage(
        newData.messages.recordingStopped,
        { name: oldData.recorder.name }
      );
    return addNotification(recordingStoppedMessage);
  }

  if (
    (oldData.isFullyConnected && !newData.isFullyConnected) ||
    (!oldData.wasKicked && newData.wasKicked)
  ) {
    return deleteNotification();
  }

  return null;
};

const muteNotificationHandler = (oldData, newData, dispatch) => {
  if (oldData.isMutedByServer === newData.isMutedByServer) return null;

  if (newData.isMutedByServer && newData.isModerator) {
    return dispatch(
      setPillNotificationWithTimeout(
        newData.messages.moderatorMutedByModerator
      )
    );
  }

  if (newData.isMutedByServer) {
    return dispatch(
      setPillNotificationWithTimeout(
        newData.messages.muteAll
      )
    );
  }

  if (newData.isAudioMuted) {
    return dispatch(
      setPillNotificationWithTimeout(
        newData.messages.unlockWhileMuted
      )
    );
  }

  if(!newData.isAudioMuted && !newData.isModerator){
    return dispatch(setPillNotificationWithTimeout(newData.messages.unlockAll));
  }

  return null;
};

const handler = (oldData, newData, dispatch) => {
  if (newData.missedNotifications !== oldData.missedNotifications) {
    updatePageTitle(newData.missedNotifications);
  }

  muteNotificationHandler(oldData, newData, dispatch);
  recordingHandler(oldData, newData, dispatch);
};

export const handlerMethods = [notificationObserverSelector, initialData, handler];
export default Observer.createObserver(...handlerMethods);
